import { mapState } from "vuex";

import comboComponent from "@/components/child/form/combo.vue";
import gridComponent from "@/components/child/grid/grid.vue";
import { GridColumn, GridColumnType } from "@/components/child/grid/gridColumn";
import modalComponent from "@/components/child/modal/modal.vue";
import multiSelectComponent from "@/components/child/multiSelect/multiSelect.vue";
import { Component } from "@/decorators";
import UsuarioModel from "@/models/usuarioModel";
import franquiaService from "@/services/franquiaService";
import grupoProdutoService from "@/services/produto/grupoProdutoService";
import tipoVendaService from "@/services/tipoVendaService";
import { AppState, SessionActions } from "@/store/store";

import DashboardComponent from "./dashboardComponent";
import periodoFiltroComponent from "./filtros/periodoFiltroComponent.vue";

// Precisa ser assim se não ocorre erro
// eslint-disable-next-line @typescript-eslint/no-var-requires
const echarts = require("echarts");

import "./dashboardComponent.scss";

@Component({
    components: {
        multiSelectComponent,
        gridComponent,
        comboComponent,
        periodoFiltroComponent,
        modalComponent,
    },
    computed: {
        ...mapState({
            usuarioLogado: (state: AppState) => state.session.usuarioLogado,
        }),
    },
})
export default class ProdutosMaisVendidos extends DashboardComponent {
    // State computed props
    usuarioLogado!: UsuarioModel;

    grupoProdutoId: number | null = null;

    private maisVendidosChart: any;
    private menosVendidosChart: any;
    maisVendidosData: any[] = [];
    menosVendidosData: any[] = [];
    tipoVenda = 0;

    private dataInicialAtual: Date | null = null;
    private dataFinalAtual: Date | null = null;

    verMaisData: any[] = [];
    verMaisTitulo = "";

    franquiaIds: Array<Record<string, any>> = [];
    defaultFranquia: Array<Record<string, any>> = [];
    franquiasOptions: Array<Record<string, any>> = [];
    tipoVendaOptions: Array<Object> = [];
    grupoProdutoOptions: Array<Object> = [];

    get gridColumns(): Array<GridColumn> {
        return [
            new GridColumn(
                "produtoDescricao",
                this.$t("__.Components.parent.dashboard.produtosMaisVendidos_vue_html.produto") as string,
                GridColumnType.String,
            ),
            new GridColumn(
                "peso",
                this.$t("__.Components.parent.dashboard.produtosMaisVendidos_vue_html.quantidade") as string,
                GridColumnType.Decimal,
            ),
            new GridColumn(
                "qtdVendas",
                this.$t("__.Components.parent.dashboard.produtosMaisVendidos_vue_html.frequencia") as string,
                GridColumnType.Integer,
            ),
        ];
    }

    get gridColumnsVerMais(): Array<GridColumn> {
        return [
            new GridColumn(
                "produtoDescricao",
                this.$t("__.Components.parent.dashboard.produtosMaisVendidos_vue_html.produto") as string,
                GridColumnType.String,
            ),
            new GridColumn(
                "grupoProdutoDescricao",
                this.$t("__.Components.parent.dashboard.produtosMaisVendidos_vue_html.grupoProdutoDescricao") as string,
                GridColumnType.String,
            ),
            new GridColumn(
                "peso",
                this.$t("__.Components.parent.dashboard.produtosMaisVendidos_vue_html.quantidade") as string,
                GridColumnType.Decimal,
            ),
            new GridColumn(
                "qtdVendas",
                this.$t("__.Components.parent.dashboard.produtosMaisVendidos_vue_html.frequencia") as string,
                GridColumnType.Integer,
            ),
        ];
    }

    private graphStyle = {
        width: "100%",
        height: "300px",
        margin: "15px 0px 0px 0px",
        // @ts-ignore
        zoom: this.compensarZoomGrafico(document.body.style.zoom),
    };

    private async mounted() {
        await this.$store.dispatch(SessionActions.LOAD_USUARIO_LOGADO);
        this.tipoVendaOptions = await tipoVendaService.comboCached(this.usuarioLogado.franquiaId ?? 0);
        this.grupoProdutoOptions = await grupoProdutoService.comboCached(this.usuarioLogado.franquiaId ?? 0);

        await this.loadFranquias().withLoading();
        const franquiaPadrao = this.franquiasOptions.find(p => p["value"] === this.usuarioLogado.franquiaId);
        if (franquiaPadrao) this.defaultFranquia.push(franquiaPadrao);
    }

    private loadReferences() {
        this.maisVendidosChart = echarts.init(this.$refs.maisVendidosChart);
        this.menosVendidosChart = echarts.init(this.$refs.menosVendidosChart);

        this.setGraficos([this.maisVendidosChart, this.menosVendidosChart]);
    }

    private toPercentageData(data: any[]) {
        const total = data.reduce((sum, item) => sum + (item.valor || 0), 0);
        if (total === 0) return data;
        return data.map(item => ({
            ...item,
            valor: Math.round((item.valor / total) * 10000) / 100,
        }));
    }

    private async onUpdateDashboard(dataInicial: Date, dataFinal: Date) {
        this.dataInicialAtual = dataInicial;
        this.dataFinalAtual = dataFinal;

        this.loadReferences();
        this.showLoadingGraficos();

        const data = await this.service
            .getDashboardProdutosVendidos(
                this.franquiaIds.map(p => p["value"]),
                dataInicial,
                dataFinal,
                this.grupoProdutoId,
                this.tipoVenda,
            )
            .withLoading()
            .resolveWithJSON<{
                maisVendidos: Array<{
                    produtoId: number;
                    produtoDescricao: string;
                    peso: number;
                    qtdVendas: number;
                    label: string;
                    valor: number;
                }>;
                menosVendidos: Array<{
                    produtoId: number;
                    produtoDescricao: string;
                    peso: number;
                    qtdVendas: number;
                    label: string;
                    valor: number;
                }>;
            }>();

        this.maisVendidosData = data.maisVendidos;
        this.menosVendidosData = data.menosVendidos;

        this.maisVendidosChart.setOption(
            this.getOpcoesGraficoPie(
                this.$t("__.ts.produtosMaisVendidos") as string,
                this.toPercentageData(this.maisVendidosData),
            ),
        );

        this.menosVendidosChart.setOption(
            this.getOpcoesGraficoPie(
                this.$t("__.ts.produtosMenosVendidos") as string,
                this.toPercentageData(this.menosVendidosData),
            ),
        );

        this.hideLoadingGraficos();
    }

    private async onVerMais(tipo: "mais" | "menos") {
        if (!this.dataInicialAtual || !this.dataFinalAtual) return;

        this.verMaisTitulo =
            tipo === "mais"
                ? (this.$t("__.ts.produtosMaisVendidos") as string)
                : (this.$t("__.ts.produtosMenosVendidos") as string);

        const todos = await this.service
            .getTodosProdutosVendidos(
                this.franquiaIds.map(p => p["value"]),
                this.dataInicialAtual,
                this.dataFinalAtual,
                this.grupoProdutoId,
                this.tipoVenda,
            )
            .withLoading()
            .resolveWithJSON<any[]>();

        this.verMaisData = tipo === "menos" ? [...todos].sort((a, b) => a.qtdVendas - b.qtdVendas) : todos;

        (this.$refs.modalVerMais as any).show();
    }

    private async loadFranquias() {
        const data = await franquiaService.comboCached(this.usuarioLogado.franquiaId ?? 0);
        this.franquiasOptions = data.map(franquia => ({
            value: franquia.id,
            text: franquia.nomeFantasia,
        }));
    }

    private onSelecionarTodas() {
        this.franquiaIds = this.franquiasOptions;
        this.defaultFranquia = this.franquiasOptions;
    }

    private onLimpar() {
        this.franquiaIds = [];
        this.defaultFranquia = [];
    }

    protected convertToString(numero: number) {
        return numero == null ? "0" : numero.toString();
    }
}
